import { z } from "zod"
import { authors } from "./blog/authors"
import { dev } from "$app/environment"

const SchemaContentMetadataBase = z.strictObject({
	title: z.string(),
	description: z.string(),
	published_time: z.string().transform(value => new Date()),
	og: z.strictObject({
		title: z.string(),
		description: z.string(),
		image: z.string()
	}),
	draft: z.boolean().optional().default(true),
	computed: z.any()
})

export const SchemaContentMetadataDocs = SchemaContentMetadataBase.extend({
	type: z.literal("blog"),
	tags: z.string().array(),
	authors: z.enum(Object.keys(authors) as unknown as [name: string, ...names: string[]]).array()
})

type Glob = {
	default: {
		render: Function
		$$render: Function
	}
	metadata: z.infer<typeof SchemaContentMetadataDocs>
}

export const globs = import.meta.glob<Glob>('./**/*.md', {
	eager: true
})

export const globByFullPathname = new Map<string, Glob>()

// Convert the glob object to an array of [pathname, module] entries,
// Sort by the 'published_time' in descending order,
// Then insert into the globByFullPathname Map
const sortedEntries = Object.entries(globs)
	.map(([pathname, module]) => {
		// Clean up the pathname as per your requirement
		pathname = pathname.replace('./', '').replace('.md', '/')
		return [pathname, module] as [string, Glob]
	})
	.sort(([, a], [, b]) => {
		// Sort by published_time in descending order
		const dateA = new Date(a.metadata.published_time).getTime()
		const dateB = new Date(b.metadata.published_time).getTime()
		return dateB - dateA
	})

// Populate the Map in sorted order
for (const [pathname, module] of sortedEntries) {
	globByFullPathname.set(pathname, module)
}

function assemblePrevNextMap() {
	let all = Array.from(globByFullPathname.entries())
		.filter(([pathname]) =>
			pathname.startsWith("blog/")
		)
		.map(([pathname, module]) => {
			const validation = SchemaContentMetadataDocs.safeParse(module.metadata)
			if (!validation.success) {
				console.error(JSON.stringify({ pathname, issues: validation.error.issues }, null, 2))
				throw new Error("invalid blog post")
			}

			return [pathname, { ...module, metadata: validation.data }] as [pathname: string, module: { default: any, metadata: z.infer<typeof SchemaContentMetadataDocs> }]
		}).sort(([, a], [, b]) => a.metadata.published_time.getTime() - b.metadata.published_time.getTime())
	if (!dev) all = all.filter(([, { metadata }]) => !metadata.draft)
		
	return {
		all,

		// Function to get the previous item based on path
		prev: (path: string) => {
			const index = all.findIndex(([pathname]) => pathname === path)
			if (index > 0) {
				return { glob: all[index - 1][1], path: '/' + all[index - 1][0] }
			}
			return null
		},

		// Function to get the next item based on path
		next: (path: string) => {
			const index = all.findIndex(([pathname]) => pathname === path)
			if (index >= 0 && index < all.length - 1) {
				return { glob: all[index + 1][1], path: '/' + all[index + 1][0] }
			}
			return null
		},

		find(slug: string) {
			const found = all.find(([pathname]) => pathname.startsWith("blog/" + slug))
			if (!found) throw new Error(`slug ${slug} not found`)
			return found[1]
		}
	}
}

export const blogPrevNextMap = assemblePrevNextMap()